/**
 * Metadata utility module for fetching and parsing webpage metadata
 * This module provides functionality to retrieve and cache metadata from external URLs
 */
import { api } from '../api/api';
import { MetadataResponse } from '../interfaces/api/content/metadata';
import axios, { AxiosResponse } from 'axios';
import { setupCache } from 'axios-cache-interceptor';
import { store } from '../redux/store';
import process from 'node:process';

/**
 * Axios instance with caching capabilities for improved performance
 */
const axiosCached = setupCache(axios);

/**
 * Parses and retrieves metadata for a given URL
 * First checks if metadata is already cached in Redux store
 * If not found, fetches it from the API endpoint and validates the response
 *
 * @param {string} href - The URL to fetch metadata for
 * @returns {Promise<MetadataResponse>} A promise that resolves to the metadata response object
 * @throws {Error} If the metadata title contains error codes or if the request fails
 */
const parseMetadata = async (href: string): Promise<MetadataResponse> => {
  try {
    const { metadata } = store.getState().chat;
    if (metadata && metadata[href]) {
      return metadata[href] as MetadataResponse;
    }
    const response: AxiosResponse<MetadataResponse> = await axiosCached.get(api.qna.metadata + href, {
      cache: {
        ttl: 100000 * 60,
      },
    });
    const wrongTitles = ['401', '402', '403', '500', '502', '503', '504', 'Error', 'click here for more info'];
    if (response.data.title && wrongTitles.some((code) => response.data.title.includes(code))) {
      throw new Error(`Invalid title found: ${response.data.title}`);
    }
    return response.data;
  } catch (error) {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.error('Error parsing metadata:', error);
    }
    throw error;
  }
};

export default parseMetadata;
